import { useState } from "react";
import cn from "classnames";
import dayjs from "dayjs";
import advanceFormat from "dayjs/plugin/advancedFormat";
// Helpers
import { calculateTax } from "../../../helpers/taxes";
// Components
import Button from "../../../components/Button/Button";
// Styles
import styles from "./OrderDetails.module.scss";
// Images
import tagIcon from "../../../assets/images/tag_icon.png";
import carretUpIcon from "../../../assets/images/carret_up.png";
import carretDownIcon from "../../../assets/images/carret_down.png";

dayjs.extend(advanceFormat);

const OrderDetails = ({
  chosenPackage,
  fieldRoutesCustomer,
  isConfirmationPage = false,
}) => {
  const [showMore, setShowMore] = useState(false);

  const handleOnShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const regularPrice = parseFloat(chosenPackage?.defaultInitialCharge) ?? 0;
  const discountPrice = parseFloat(chosenPackage?.minimumInitialCharge) ?? 0;
  const regularPriceTax = calculateTax(
    discountPrice,
    fieldRoutesCustomer?.taxRate
  );
  const subscriptionRegularPrice =
    parseFloat(chosenPackage?.defaultCharge) ?? 0;
  const subscriptionDiscountPrice = parseFloat(
    chosenPackage?.minimumRecurringCharge
  );

  const subscriptionPriceTax = calculateTax(
    subscriptionDiscountPrice,
    fieldRoutesCustomer?.taxRate
  );

  const packageName = chosenPackage?.description;

  if (!fieldRoutesCustomer) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div>
        <h2 className={styles.title}>YOUR ORDER</h2>
        <div className={styles.orderDivider} />
      </div>

      <div className={styles.row}>
        <div>
          <p>{packageName}: Initial service visit</p>
          <ul className={styles.initialServiceList}>
            <li>One-Time payment</li>
            <li>Non-refundable</li>
          </ul>
        </div>
        <p>${regularPrice.toFixed(2)}</p>
      </div>

      <div className={cn(styles.row, styles.discountRow)}>
        <p className={styles.specialDiscount}>
          <img src={tagIcon} alt="Special Online Discount" /> Special Online
          Discount
        </p>
        <p>-${(regularPrice - discountPrice).toFixed(2)}</p>
      </div>

      <div className={styles.subtotalSection}>
        <div className={styles.row}>
          <p className={styles.bold}>Subtotal</p>
          <p className={styles.bold}>${discountPrice.toFixed(2)}</p>
        </div>

        <div className={styles.row}>
          <p className={styles.bold}>
            Tax{" "}
            <span className={styles.taxRatePercent}>
              ({(parseFloat(fieldRoutesCustomer.taxRate) * 100).toFixed(2)}%)
            </span>
          </p>
          <p className={styles.bold}>${regularPriceTax.toFixed(2)}</p>
        </div>
      </div>

      <div className={cn(styles.row, styles.totalRow)}>
        <p>Total {isConfirmationPage ? "paid" : "due"} today</p>
        <p>${(discountPrice + regularPriceTax).toFixed(2)}</p>
      </div>

      <div
        className={cn(
          styles.row,
          styles.subscriptionTotalRow,
          styles.subscriptionTotalRowMobile,
          {
            [styles.hide]: showMore || isConfirmationPage,
          }
        )}
      >
        <p>Total due {dayjs().add(1, "month").format("MMMM Do")}</p>
        <p>${(subscriptionDiscountPrice + subscriptionPriceTax).toFixed(2)}</p>
      </div>

      <div
        className={cn(styles.mobileRoot, {
          [styles.show]: showMore || isConfirmationPage,
        })}
      >
        <div className={styles.dividerWrapper}>
          <div className={styles.blueDivider} />
        </div>

        <div className={cn(styles.row, styles.subscriptionRow)}>
          <div className={styles.subscriptionTitleAndPrice}>
            <p>{packageName}</p>
            <p className={styles.bold}>${subscriptionRegularPrice}/mo</p>
          </div>

          <ul className={styles.initialServiceList}>
            <li>12-month commitment</li>
            <li>
              Charged monthly to the payment method on file on the{" "}
              {dayjs().format("Do")} day of the month
            </li>
            <li>Bi-monthly maintenance</li>
          </ul>
        </div>

        <div className={cn(styles.row, styles.discountRow)}>
          <p className={styles.specialDiscount}>
            <img src={tagIcon} alt="Special Online Discount" /> Special Online
            Discount
          </p>
          <p>
            -$
            {(subscriptionRegularPrice - subscriptionDiscountPrice).toFixed(2)}
          </p>
        </div>

        <div className={styles.subtotalSection}>
          <div className={styles.row}>
            <p className={styles.bold}>Subtotal</p>
            <p className={styles.bold}>
              ${subscriptionDiscountPrice.toFixed(2)}
            </p>
          </div>

          <div className={styles.row}>
            <p className={styles.bold}>
              Tax{" "}
              <span className={styles.taxRatePercent}>
                ({(parseFloat(fieldRoutesCustomer.taxRate) * 100).toFixed(2)}%)
              </span>
            </p>
            <p className={styles.bold}>${subscriptionPriceTax.toFixed(2)}</p>
          </div>
        </div>

        <div className={cn(styles.row, styles.subscriptionTotalRow)}>
          <p>Total due {dayjs().add(1, "month").format("MMMM Do")}</p>
          <p>
            ${(subscriptionDiscountPrice + subscriptionPriceTax).toFixed(2)}
          </p>
        </div>
      </div>

      <div
        className={cn(styles.mobileButton, {
          [styles.hide]: isConfirmationPage,
        })}
      >
        <Button htmlType="button" type="outline" onClick={handleOnShowMore}>
          <img
            src={showMore ? carretUpIcon : carretDownIcon}
            alt={showMore ? "show less" : "show more"}
          />
          See {showMore ? "Less" : "More"}
        </Button>
      </div>
    </div>
  );
};

export default OrderDetails;
